/**
 * 🛡️ Safe API Helpers
 * Utilities pour des appels API robustes avec timeout, retry, et gestion d'erreurs
 * ✅ Uses native fetch to avoid rrweb-recorder clone conflicts
 * 🔒 P0 EXECUTION CONTEXT: Injection automatique du header X-Execution-Context-Id
 * 
 * 🔒 P0 FIX (07/01/2025): Utilise window.fetch (patché par usePageExecutionContext)
 * pour garantir l'injection du header sur 100% des appels
 */

/**
 * 🔒 P0 EXECUTION CONTEXT: Fetch avec injection automatique du header
 * Utilise window.__nativeFetch__ (sauvegardé dans index.html) comme source unique
 * Le header est injecté AUTOMATIQUEMENT si window.__currentExecutionContextId existe
 * 
 * 🔒 P0 FIX (Jan 2026): Toujours utiliser window.__nativeFetch__ pour éviter
 * les conflits avec rrweb et les erreurs "Response body already used"
 */
const getContextAwareFetch = () => {
  if (typeof window !== 'undefined') {
    // 🔒 Utiliser TOUJOURS le fetch natif sauvegardé avant tout patch
    return (window.__nativeFetch__ || window.fetch).bind(window);
  }
  return fetch;
};

/**
 * 🔒 Legacy alias pour compatibilité avec safeFetchJSON
 * Pointe vers le même fetch patché pour injection du header
 */
const getNativeFetch = getContextAwareFetch;

/**
 * Effectue un appel fetch avec timeout
 * @param {string} url - URL de l'API
 * @param {object} options - Options fetch
 * @param {number} timeout - Timeout en ms (défaut: 30000)
 * @returns {Promise<Response>}
 * 
 * 🔒 P0 FIX: Injection manuelle du header X-Execution-Context-Id
 */
export const fetchWithTimeout = async (url, options = {}, timeout = 30000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  const contextFetch = getContextAwareFetch();
  
  // 🔒 P0 FIX: Injection manuelle du header X-Execution-Context-Id
  const finalHeaders = { ...options.headers };
  if (typeof window !== 'undefined' && window.__currentExecutionContextId) {
    finalHeaders['X-Execution-Context-Id'] = window.__currentExecutionContextId;
  }
  
  try {
    const response = await contextFetch(url, {
      ...options,
      headers: finalHeaders,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    // 🔒 P0: Traitement silencieux des erreurs 499 (contexte invalidé)
    if (error.status === 499 || error.message?.includes('499')) {
      console.log('[apiHelpers] ⏹️ Request cancelled - context invalidated');
      throw error; // Re-throw mais silencieux côté UI
    }
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeout}ms`);
    }
    throw error;
  }
};

/**
 * Effectue un appel fetch avec retry logic
 * @param {string} url - URL de l'API
 * @param {object} options - Options fetch
 * @param {number} maxRetries - Nombre maximum de tentatives (défaut: 3)
 * @param {number} initialDelay - Délai initial en ms (défaut: 1000)
 * @returns {Promise<Response>}
 */
export const fetchWithRetry = async (
  url,
  options = {},
  maxRetries = 3,
  initialDelay = 1000
) => {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      console.log(`[fetchWithRetry] Attempt ${attempt + 1}/${maxRetries} for ${url}`);
      const response = await fetchWithTimeout(url, options);
      
      // Si succès, retourner immédiatement
      if (response.ok || response.status < 500) {
        return response;
      }
      
      // Si erreur serveur (5xx), retry
      console.warn(`[fetchWithRetry] Server error ${response.status}, retrying...`);
      lastError = new Error(`Server error: ${response.status}`);
      
    } catch (error) {
      console.warn(`[fetchWithRetry] Attempt ${attempt + 1} failed:`, error.message);
      lastError = error;
    }
    
    // Si ce n'est pas la dernière tentative, attendre avant de retry
    if (attempt < maxRetries - 1) {
      const delay = initialDelay * Math.pow(2, attempt); // Backoff exponentiel
      console.log(`[fetchWithRetry] Waiting ${delay}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  // Toutes les tentatives ont échoué
  throw lastError || new Error('All retry attempts failed');
};

/**
 * 🔒 Effectue un appel API sécurisé avec parsing unique du body
 * @param {string} url - URL de l'API
 * @param {object} options - Options fetch
 * @param {boolean} useRetry - Utiliser retry logic (défaut: true)
 * @returns {Promise<{data: any, response: Response}>}
 */
export const safeFetch = async (url, options = {}, useRetry = true) => {
  try {
    // Appel avec ou sans retry
    const response = useRetry
      ? await fetchWithRetry(url, options)
      : await fetchWithTimeout(url, options);
    
    // ✅ CRITICAL: Parse le body UNE SEULE FOIS - PAS de clone!
    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      console.error(`[safeFetch] Failed to parse JSON from ${url}:`, parseError);
      throw new Error(`Invalid JSON response from server`);
    }
    
    // Vérifier le statut APRÈS parsing
    if (!response.ok) {
      const errorMessage = data?.detail || data?.message || `HTTP Error ${response.status}`;
      throw new Error(errorMessage);
    }
    
    // Retourner data ET response pour flexibilité
    return { data, response };
    
  } catch (error) {
    // Log l'erreur pour debugging
    console.error(`[safeFetch] Error for ${url}:`, error);
    
    // Re-throw avec contexte
    throw new Error(`API call failed: ${error.message}`);
  }
};

/**
 * 🛡️ Safe Fetch JSON - Version ULTRA-ROBUSTE (anti clone/double lecture/rrweb)
 * @param {string} url - URL de l'API
 * @param {object} options - Options fetch
 * @returns {Promise<any>} - Données JSON parsées
 * 
 * 🔒 P0 FIX (Jan 2025): Injection manuelle du header X-Execution-Context-Id
 * car le fetch natif bypass le patch de usePageExecutionContext
 */
export const safeFetchJSON = async (url, options = {}) => {
  const nativeFetch = getNativeFetch();
  console.log('✅ ultra-robust fetch (native from window.__nativeFetch__) active for:', url);
  
  try {
    // ✅ CRITICAL: Use NATIVE fetch directly to bypass rrweb wrapper
    const controller = new AbortController();
    // 🔧 P0 FIX (Fev 2026) — Timeout 60s → 180s pour laisser une vraie
    // marge au-dessus du backend (120s). Avant : 60s frontend = 60s
    // backend → marge zéro → tout appel TTS au bord de la limite était
    // tué par le client. Désormais : backend abandonne à 120s, frontend
    // attend jusqu'à 180s → l'erreur backend a le temps de remonter.
    const SAFE_FETCH_TIMEOUT_MS = 180000;
    const timeoutId = setTimeout(() => controller.abort(), SAFE_FETCH_TIMEOUT_MS);
    
    // 🔒 P0 FIX: Injection manuelle du header X-Execution-Context-Id
    // Nécessaire car le fetch natif ne passe pas par le patch de usePageExecutionContext
    const finalHeaders = { ...options.headers };
    if (typeof window !== 'undefined' && window.__currentExecutionContextId) {
      finalHeaders['X-Execution-Context-Id'] = window.__currentExecutionContextId;
      console.log(`🔒 [safeFetchJSON] Injecting execution context: ${window.__currentExecutionContextId.slice(0, 8)}...`);
    }
    
    let response;
    try {
      // ✅ Use nativeFetch instead of window.fetch to avoid rrweb interception
      response = await nativeFetch(url, {
        ...options,
        headers: finalHeaders,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        throw new Error(`Request timeout after ${Math.round(SAFE_FETCH_TIMEOUT_MS / 1000)}s`);
      }
      throw fetchError;
    }
    
    // ✅ CRITICAL FIX: Read body as text IMMEDIATELY, no cloning
    let responseText;
    try {
      responseText = await response.text();
    } catch (readError) {
      console.error(`[safeFetchJSON] Failed to read response:`, readError);
      throw new Error(`Failed to read server response`);
    }

    // 🔧 P0 FIX (Fev 2026) — Détecter les réponses NON-JSON AVANT parse.
    // Quand le proxy ingress K8s renvoie 502/503/504 (backend down, en
    // restart, ou OOM-killed), il sert du texte brut type :
    //   "The preview environment is not responding. It may be starting up."
    // Sans ce check, JSON.parse crashe et le user voit
    // "Invalid JSON response from server" qui MASQUE le vrai statut HTTP.
    // Désormais on remonte une Error claire avec statut + endpoint + extrait.
    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    const looksLikeJson = contentType.includes('application/json')
      || contentType.includes('+json')
      || responseText.startsWith('{')
      || responseText.startsWith('[');

    if (!response.ok && !looksLikeJson) {
      const snippet = (responseText || '').trim().slice(0, 160);
      console.error(
        `[safeFetchJSON] Non-JSON ${response.status} from ${url} : ${snippet}`,
      );
      const proxyErr = new Error(
        `HTTP ${response.status} from server (${snippet || 'no body'})`,
      );
      proxyErr.status = response.status;
      proxyErr.endpoint = url;
      proxyErr.bodyPreview = snippet;
      throw proxyErr;
    }

    // ✅ Parse JSON from text (no network involved)
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error(`[safeFetchJSON] JSON parse error:`, parseError);
      console.error(`[safeFetchJSON] Response text:`, responseText.substring(0, 200));
      // Inclure statut HTTP dans le message pour ne plus masquer la cause.
      throw new Error(
        `Invalid JSON response from server (HTTP ${response.status})`,
      );
    }
    
    // ✅ Check status AFTER parsing (we have the data now)
    if (!response.ok) {
      console.error(`[safeFetchJSON] API error ${response.status}:`, data);
      
      // ✅ CRITICAL: Provide detailed error for 422 with proper formatting
      if (response.status === 422) {
        let validationDetails = data?.detail || data?.errors || 'Validation failed';
        
        // Handle array of validation errors
        if (Array.isArray(validationDetails)) {
          validationDetails = validationDetails.map(err => {
            if (typeof err === 'object') {
              return err.msg || err.message || JSON.stringify(err);
            }
            return err;
          }).join(', ');
        } else if (typeof validationDetails === 'object') {
          // Handle object validation errors
          validationDetails = JSON.stringify(validationDetails, null, 2);
        }
        
        console.error(`[safeFetchJSON] 422 Validation Error:`, validationDetails);
        throw new Error(`Erreur de validation: ${validationDetails}`);
      }
      
      const errorMessage = data?.detail || data?.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }
    
    console.log('✅ ultra-robust fetch successful for:', url);
    return data;
    
  } catch (error) {
    console.error(`[safeFetchJSON] Error for ${url}:`, error.message);
    throw error;
  }
};

/**
 * 🎵 Safe Audio Player avec gestion d'erreurs
 */
export class SafeAudioPlayer {
  constructor() {
    this.currentAudio = null;
    this.isPlaying = false;
  }
  
  /**
   * Stop l'audio en cours s'il existe
   */
  stop() {
    if (this.currentAudio) {
      try {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
        this.currentAudio = null;
        this.isPlaying = false;
        console.log('[SafeAudioPlayer] Audio stopped');
      } catch (error) {
        console.warn('[SafeAudioPlayer] Error stopping audio:', error);
      }
    }
  }
  
  /**
   * Play un nouvel audio (stop l'ancien si nécessaire)
   * @param {string} audioUrl - URL de l'audio
   * @param {object} options - Options (onEnded, onError, autoplay)
   * @returns {Promise<Audio>}
   */
  async play(audioUrl, options = {}) {
    const {
      onEnded = () => {},
      onError = () => {},
      autoplay = true
    } = options;
    
    // Stop l'audio précédent
    this.stop();
    
    // Créer nouvel audio
    const audio = new Audio(audioUrl);
    this.currentAudio = audio;
    
    // Event handlers
    audio.onended = () => {
      console.log('[SafeAudioPlayer] Audio ended');
      this.isPlaying = false;
      onEnded();
    };
    
    audio.onerror = (error) => {
      console.error('[SafeAudioPlayer] Audio error:', error);
      this.isPlaying = false;
      onError(error);
    };
    
    // Essayer de jouer
    if (autoplay) {
      try {
        await audio.play();
        this.isPlaying = true;
        console.log('[SafeAudioPlayer] Audio playing');
      } catch (error) {
        console.warn('[SafeAudioPlayer] Autoplay blocked:', error);
        this.isPlaying = false;
        throw new Error('Autoplay blocked by browser');
      }
    }
    
    return audio;
  }
  
  /**
   * Vérifie si un audio est en cours de lecture
   */
  getIsPlaying() {
    return this.isPlaying;
  }
}

/**
 * 🔐 Request Deduplicator
 * Empêche les appels dupliqués à la même API
 */
export class RequestDeduplicator {
  constructor() {
    this.activeRequests = new Map();
    this.requestTimestamps = new Map();
  }
  
  /**
   * Vérifie si une requête est déjà en cours
   * @param {string} requestId - ID unique de la requête
   * @param {number} minInterval - Intervalle minimum en ms (défaut: 2000)
   * @returns {boolean}
   */
  isDuplicate(requestId, minInterval = 2000) {
    // Check 1: Requête active en cours ?
    if (this.activeRequests.has(requestId)) {
      console.warn(`[Deduplicator] ⛔ BLOCKED - Request ${requestId} already active`);
      return true;
    }
    
    // Check 2: Requête récente (dans minInterval) ?
    const lastTimestamp = this.requestTimestamps.get(requestId);
    if (lastTimestamp) {
      const elapsed = Date.now() - lastTimestamp;
      if (elapsed < minInterval) {
        console.warn(`[Deduplicator] ⛔ BLOCKED - Request ${requestId} too recent (${elapsed}ms ago)`);
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Marque une requête comme active
   * @param {string} requestId - ID unique de la requête
   */
  markActive(requestId) {
    this.activeRequests.set(requestId, true);
    this.requestTimestamps.set(requestId, Date.now());
    console.log(`[Deduplicator] 🔒 LOCKED - Request ${requestId} marked as active`);
  }
  
  /**
   * Libère une requête
   * @param {string} requestId - ID unique de la requête
   */
  release(requestId) {
    this.activeRequests.delete(requestId);
    console.log(`[Deduplicator] 🔓 UNLOCKED - Request ${requestId} released`);
  }
  
  /**
   * Reset complet (pour tests)
   */
  reset() {
    this.activeRequests.clear();
    this.requestTimestamps.clear();
    console.log('[Deduplicator] 🔄 RESET - All requests cleared');
  }
}

// Export singleton instances
export const audioPlayer = new SafeAudioPlayer();
export const requestDeduplicator = new RequestDeduplicator();
